import type { FormEvent, ReactElement, ReactNode } from 'react'
import { useState } from 'react'
import { chartCss } from '~/charts/theme'
import { AUTH_NARROW_PX } from '~/charts/tokens'
import {
  hasAuthenticator,
  invitationFromLocation,
  useLogin,
  useRegister,
  useRegistration,
} from '~/state/auth'

/**
 * What a connection may do, in one sentence, on every screen that mentions it.
 *
 * Written once and read from here rather than typed per page: the mockups said
 * "read-only, always" on the desktop panel and "it can never move money" on the
 * phone, and both are false of Nordnet, whose session id is an ordinary trading
 * credential — `SOURCE_DEFINITIONS` says so where the household types it in. Two
 * screens disagreeing about what this product can reach would be worse than one
 * of them being wrong, so there is one sentence and it names the exception.
 */
export const CONNECTION_REACH =
  'Bank, broker and chain connections read balances and nothing else. Nordnet is the exception: it issues no read-only credential, so the session id it hands out can trade.'

/** As the terms and the privacy notice name it. Networthy is the product; 1337 ApS is the company. */
export const OPERATOR = 'Networthy Finance, by 1337 ApS'

const TERMS_HREF = 'https://networthy.finance/terms'
const PRIVACY_HREF = 'https://networthy.finance/privacy'

const PAGE_CSS = `
.nw-auth {
  align-items: stretch; display: flex; max-width: none; min-height: 100vh; padding: 0;
}
.nw-auth h1 {
  font-size: var(--nw-auth-title); letter-spacing: var(--nw-track-display); line-height: 1.15;
  margin: 0;
}
.nw-auth h2 { font-size: var(--nw-text-title); letter-spacing: var(--nw-track-title); }
/* A default, matched through :where() so that any class below beats it by its
   class alone — the same arrangement the system's own controls are given. */
.nw-auth :where(p) { margin: 0; }

.nw-auth-form {
  background: var(--nw-card); box-sizing: border-box; display: flex;
  flex: 0 0 var(--nw-auth-col); flex-direction: column;
  padding: var(--nw-auth-edge-top) var(--nw-auth-edge) var(--nw-auth-edge-bottom);
  width: var(--nw-auth-col);
}
/* The mockups inset the wordmark and the footer to 48 and the form to 128. */
.nw-auth-body {
  display: flex; flex: 1 1 auto; flex-direction: column; justify-content: center;
  padding: var(--nw-space-8) calc(var(--nw-auth-pad-x) - var(--nw-auth-edge));
}
.nw-auth-mark { align-items: center; display: flex; flex: none; gap: 10px; }
.nw-auth-mark b {
  font-size: var(--nw-text-wordmark); font-weight: var(--nw-weight-figure); letter-spacing: var(--nw-track-wordmark);
}
.nw-auth-step {
  color: var(--nw-faint); font-size: var(--nw-text-chip); font-weight: var(--nw-weight-strong);
  letter-spacing: var(--nw-track-label); margin: 0 0 10px; text-transform: uppercase;
}
.nw-auth-lede {
  color: var(--nw-muted); font-size: var(--nw-auth-lede); line-height: 1.55; margin: 10px 0 0;
}
.nw-auth-footer {
  color: var(--nw-faint); flex: none; font-size: var(--nw-text-chip); margin: 0;
}
.nw-auth-footer a {
  border-bottom: var(--nw-hairline-width) solid var(--nw-hairline); color: var(--nw-faint);
  text-decoration: none;
}

.nw-auth-field { display: flex; flex-direction: column; gap: 7px; margin-top: 28px; }
.nw-auth-field label { font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); }
/* The 16 is not a size taken off the type scale and must not be replaced by one:
   iOS zooms the page when a field under 16px takes focus, and it does it on the
   one screen nobody can get past. A field elsewhere reads --nw-text-field at 15,
   which is the size this would otherwise be. */
.nw-auth-field input {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-field-radius); box-sizing: border-box; color: var(--nw-text);
  font: inherit; font-size: 16px; height: var(--nw-h-control-lg); padding: 0 16px; width: 100%;
}
/* The 2px edge and its 4px ring are the system's focus treatment at the size the
   sign-in field is drawn: the mockups show every field on these pages wearing it. */
.nw-auth-field input:focus-visible {
  border-color: var(--nw-focus); border-width: 2px; box-shadow: 0 0 0 4px var(--nw-focus-ring);
  outline: 0; padding: 0 15px;
}
.nw-auth-field small { color: var(--nw-faint); font-size: var(--nw-text-chip); }
.nw-auth-field + .nw-auth-field { margin-top: 18px; }

.nw-auth-submit {
  align-items: center; background: var(--nw-accent); border: 0;
  border-radius: var(--nw-radius-full); color: var(--nw-accent-ink); cursor: pointer;
  display: flex; font: inherit; font-size: var(--nw-auth-lede);
  font-weight: var(--nw-weight-strong); gap: 9px; height: var(--nw-h-control-lg);
  justify-content: center; margin-top: 20px; width: 100%;
}
.nw-auth-submit:hover:enabled { background: var(--nw-accent-hover); }
.nw-auth-submit:disabled { cursor: not-allowed; opacity: .55; }
.nw-auth-quiet {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-full); color: var(--nw-text); cursor: pointer; font: inherit;
  font-size: var(--nw-auth-lede); font-weight: var(--nw-weight-strong);
  height: var(--nw-h-control-lg); margin-top: 26px; width: 100%;
}
.nw-auth-quiet:hover:enabled { background: var(--nw-hover); }
.nw-auth-quiet:disabled { cursor: not-allowed; opacity: .55; }

.nw-auth-reassure {
  background: var(--nw-preview); border-radius: var(--nw-radius-panel); display: flex; gap: 13px;
  margin-top: 24px; padding: 16px 18px;
}
.nw-auth-reassure svg { color: var(--nw-accent); flex: none; }
.nw-auth-reassure b { display: block; font-size: var(--nw-text-body); font-weight: var(--nw-weight-strong); }
.nw-auth-reassure span span {
  color: var(--nw-body-ink); display: block; font-size: var(--nw-text-cell); line-height: 1.55;
  margin-top: 4px;
}

.nw-auth-stakes {
  border-top: var(--nw-hairline-width) solid var(--nw-row-rule); margin-top: 22px; padding-top: 16px;
}
.nw-auth-stakes p { color: var(--nw-muted); font-size: var(--nw-text-cell); line-height: 1.55; }
.nw-auth-stakes p + p { margin-top: 8px; }
.nw-auth-stakes b { color: var(--nw-text); }

.nw-auth-swap { color: var(--nw-muted); font-size: var(--nw-text-body); margin-top: 24px; }
.nw-auth-swap button {
  background: none; border: 0; color: var(--nw-accent); cursor: pointer; font: inherit;
  font-weight: var(--nw-weight-strong); min-height: 0; padding: 0; text-decoration: none;
}
.nw-auth-swap button:hover { color: var(--nw-accent-hover); }

.nw-auth-who {
  align-items: center; align-self: flex-start; background: var(--nw-ground);
  border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-full);
  display: flex; gap: 10px; margin-top: 14px; padding: 7px 8px 7px 14px;
}
.nw-auth-who span { font-size: var(--nw-text-body); font-weight: var(--nw-weight-medium); }
.nw-auth-who button {
  background: var(--nw-card); border: var(--nw-hairline-width) solid var(--nw-hairline);
  border-radius: var(--nw-radius-full); color: var(--nw-accent); cursor: pointer; font: inherit;
  font-size: var(--nw-text-cell); font-weight: var(--nw-weight-strong); min-height: 0;
  padding: 4px 12px;
}

.nw-auth-badge {
  align-items: center; background: var(--nw-preview); border-radius: var(--nw-radius-full);
  color: var(--nw-accent); display: flex; height: var(--nw-auth-mark);
  justify-content: center; margin-top: 26px; width: var(--nw-auth-mark);
}
.nw-auth-progress { align-items: center; display: flex; gap: 12px; margin-top: 22px; }
.nw-auth-progress span:first-child {
  background: var(--nw-row-rule); border-radius: var(--nw-radius-full); display: block;
  flex: 1 1 auto; height: var(--nw-auth-bar-h); overflow: hidden; position: relative;
}
.nw-auth-progress i {
  background: var(--nw-accent); border-radius: var(--nw-radius-full); display: block;
  height: var(--nw-auth-bar-h); left: 24%; position: absolute; top: 0; width: 38%;
}
.nw-auth-progress em {
  color: var(--nw-muted); font-size: var(--nw-text-cell); font-style: normal;
  font-weight: var(--nw-weight-medium);
}
.nw-auth-key {
  border-top: var(--nw-hairline-width) solid var(--nw-row-rule); color: var(--nw-faint);
  font-size: var(--nw-text-chip); line-height: 1.55; margin-top: 22px; padding-top: 18px;
}

.nw-auth-pitch {
  background: var(--nw-pitch); box-sizing: border-box; color: var(--nw-pitch-ink); display: flex;
  flex: 1 1 var(--nw-auth-col); flex-direction: column; justify-content: center;
  /* A flex item's min-width defaults to auto, so its min-content floor overrides
     the flex-basis above and the two columns overflow the width they were sized
     for. min-width: 0 lets the basis actually govern. */
  min-width: 0;
  padding: var(--nw-auth-edge-top) var(--nw-auth-pitch-pad-x);
}
.nw-auth-pitch h2 {
  font-size: var(--nw-auth-pitch-title); font-weight: var(--nw-weight-figure);
  letter-spacing: var(--nw-track-display); line-height: 1.25; margin: 0;
}
.nw-auth-pitch > p {
  color: var(--nw-pitch-body); font-size: var(--nw-auth-lede); line-height: 1.6; margin: 14px 0 0;
}
.nw-auth-sample {
  background: var(--nw-pitch-card); border: var(--nw-hairline-width) solid var(--nw-pitch-edge);
  border-radius: 18px; margin-top: 32px; padding: 22px 24px;
}
.nw-auth-sample > p:first-child {
  align-items: center; color: var(--nw-pitch-body); display: flex; font-size: var(--nw-text-cell);
  font-weight: var(--nw-weight-medium); gap: 10px;
}
/* The figure below is drawn, not read. Saying so is cheaper than a visitor
   mistaking somebody's illustration for the total they came here to see. */
.nw-auth-sample em {
  background: var(--nw-pitch-edge); border-radius: var(--nw-radius-full); color: var(--nw-pitch-mark);
  font-size: var(--nw-text-meta); font-style: normal; font-weight: var(--nw-weight-strong);
  letter-spacing: var(--nw-track-label); padding: 3px 8px; text-transform: uppercase;
}
.nw-auth-total {
  font-size: var(--nw-auth-pitch-title); font-weight: var(--nw-weight-figure);
  letter-spacing: var(--nw-track-display); line-height: 1; margin-top: 8px;
}
.nw-auth-total i {
  color: var(--nw-pitch-body); font-size: var(--nw-text-ore); font-style: normal;
  font-weight: var(--nw-weight-medium);
}
.nw-auth-sample svg { display: block; margin-top: 16px; }
.nw-auth-points { margin-top: 30px; }
.nw-auth-points > div {
  border-top: var(--nw-hairline-width) solid var(--nw-pitch-edge); display: flex; gap: 14px;
  padding: 12px 0;
}
.nw-auth-points svg { color: var(--nw-pitch-mark); flex: none; margin-top: 2px; }
.nw-auth-points b { display: block; font-size: var(--nw-text-body); font-weight: var(--nw-weight-strong); }
.nw-auth-points p {
  color: var(--nw-pitch-body); font-size: var(--nw-text-cell); line-height: 1.5; margin-top: 3px;
}
.nw-auth-steps { margin-top: 30px; }
.nw-auth-steps > div {
  border-top: var(--nw-hairline-width) solid var(--nw-pitch-edge); display: flex; gap: 18px;
  padding: 16px 0;
}
.nw-auth-steps > div > span:first-child {
  align-items: center; background: var(--nw-pitch-card);
  border: var(--nw-hairline-width) solid var(--nw-pitch-edge); border-radius: var(--nw-radius-full);
  color: var(--nw-pitch-mark); display: flex; flex: none; font-size: var(--nw-text-body);
  font-weight: var(--nw-weight-figure); height: 32px; justify-content: center; width: 32px;
}
.nw-auth-steps b { display: block; font-size: var(--nw-auth-lede); font-weight: var(--nw-weight-strong); }
.nw-auth-steps p {
  color: var(--nw-pitch-body); font-size: var(--nw-text-cell); line-height: 1.55; margin-top: 4px;
}
.nw-auth-reach {
  background: var(--nw-pitch-card); border: var(--nw-hairline-width) solid var(--nw-pitch-edge);
  border-radius: var(--nw-radius-lg); color: var(--nw-pitch-body); display: flex; gap: 14px;
  font-size: var(--nw-text-cell); line-height: 1.55; margin-top: 26px; padding: 18px 20px;
}
.nw-auth-reach svg { color: var(--nw-pitch-mark); flex: none; margin-top: 2px; }

/* The system draws its focus ring at the inner radius; three of these are pills. */
.nw-auth-submit:focus-visible, .nw-auth-quiet:focus-visible,
.nw-auth-who button:focus-visible, .nw-auth-swap button:focus-visible {
  border-radius: var(--nw-radius-full);
}

@media (max-width: ${AUTH_NARROW_PX}px) {
  .nw-auth { display: block; }
  .nw-auth-form {
    padding: var(--nw-auth-phone-top) var(--nw-auth-phone-gutter) 0; width: auto;
  }
  .nw-auth-body { justify-content: flex-start; padding: var(--nw-space-6) 0 0; }
  .nw-auth h1 { font-size: var(--nw-auth-title-phone); }
  .nw-auth-field input { height: var(--nw-auth-field-h-phone); }
  .nw-auth-submit, .nw-auth-quiet { height: var(--nw-auth-field-h-phone); }
  .nw-auth-footer { margin-top: var(--nw-space-6); }
  /* Everything the panel says beyond the one sentence is a column's worth of
     reading, and the phone mockup keeps none of it. */
  .nw-auth-pitch {
    display: block; margin: 20px var(--nw-space-4) var(--nw-space-4);
    padding: 0; border-radius: 18px;
  }
  .nw-auth-lead { display: none; }
  .nw-auth-reach { border: 0; border-radius: 18px; margin: 0; padding: 16px 18px; }
}
`

/**
 * There is no password field on this page and there is not meant to be one.
 * The dashboard holds years of daily positions that cannot be collected again,
 * and a shared secret that can be phished, reused or guessed is the wrong lock
 * for a door nobody can rebuild what is behind.
 */
export function Login({
  onInvitationRedeemed,
}: {
  /**
   * Registering with a token redeems it server-side, in the same request —
   * there is no separate accept call to make. `App` holds its own copy of the
   * token to drive that call for everyone else who arrives signed in with one
   * still pending, so it has to be told when this page already spent it.
   */
  onInvitationRedeemed?: () => void
} = {}): ReactElement {
  // Read once, when the page opens. The address it is read off is rewritten
  // while this page is on screen — signing in resumes where the visitor was
  // headed — and a token re-read afterwards is a token gone.
  const [invitation] = useState(invitationFromLocation)
  const [enrolling, setEnrolling] = useState(invitation !== undefined)
  const authenticator = hasAuthenticator()

  if (enrolling) {
    return (
      <Enrol
        {...(invitation === undefined ? {} : { invitation })}
        authenticator={authenticator}
        onSignIn={() => setEnrolling(false)}
        {...(onInvitationRedeemed === undefined ? {} : { onInvitationRedeemed })}
      />
    )
  }

  return <SignIn authenticator={authenticator} onEnrol={() => setEnrolling(true)} />
}

function SignIn({
  authenticator,
  onEnrol,
}: {
  authenticator: boolean
  onEnrol: () => void
}): ReactElement {
  const disabled = !authenticator
  const [email, setEmail] = useState('')
  const [asking, setAsking] = useState(false)
  const login = useLogin()

  const ask = (): void => {
    setAsking(true)
    login.mutate({ email: email.trim() })
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    ask()
  }

  if (asking) {
    return (
      <Shell pitch={<Assurance />}>
        <Ceremony
          email={email.trim()}
          pending={login.isPending}
          error={login.error}
          onRetry={ask}
          onChange={() => {
            setAsking(false)
            login.reset()
          }}
        />
      </Shell>
    )
  }

  return (
    <Shell pitch={<Assurance />}>
      <p className="nw-auth-step">Step 1 of 2</p>
      <h1>Welcome back</h1>
      <p className="nw-auth-lede">Type your email. Your device does the rest.</p>

      {!authenticator && <NoAuthenticator />}

      <form onSubmit={submit}>
        <div className="nw-auth-field">
          <label htmlFor="nw-signin-email">Email</label>
          <input
            id="nw-signin-email"
            type="email"
            autoComplete="username webauthn"
            aria-describedby="nw-signin-email-hint"
            value={email}
            disabled={disabled || login.isPending}
            onChange={(event) => setEmail(event.target.value)}
          />
          <small id="nw-signin-email-hint">
            It names which passkeys your device may offer. An address nobody has registered here is
            answered exactly like one that has.
          </small>
        </div>

        <button
          type="submit"
          className="nw-auth-submit"
          disabled={disabled || login.isPending || email.trim() === ''}
        >
          Continue
          <Arrow />
        </button>
      </form>

      <Reassurance
        title="Networthy uses passkeys only"
        body="Your fingerprint, face or device PIN. There is no password to choose, forget, reuse or leak."
      />
      <Stakes />

      <p className="nw-auth-swap">
        New to Networthy?{' '}
        <button type="button" onClick={onEnrol}>
          Create an account
        </button>
      </p>
    </Shell>
  )
}

/**
 * Registration and invitation acceptance are the same ceremony with and without
 * a token, because that is what the server makes of them: it admits an
 * invitation that matches the address, or the first person to reach an
 * installation nobody can yet sign in to, and nothing else.
 *
 * The form is offered even where the copy says the service is by invitation,
 * because this page cannot tell somebody arriving first at a fresh deployment
 * from a stranger, and the server can. What it must not do is explain that
 * arrangement to a visitor, who is here to get in rather than to read about how
 * the deployment was set up.
 */
function Enrol({
  invitation,
  authenticator,
  onSignIn,
  onInvitationRedeemed,
}: {
  invitation?: string
  authenticator: boolean
  onSignIn: () => void
  onInvitationRedeemed?: () => void
}): ReactElement {
  const disabled = !authenticator
  const invited = invitation !== undefined
  const registration = useRegistration()
  const open = registration.data?.signup === 'open'
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [asking, setAsking] = useState(false)
  const register = useRegister()

  const ask = (): void => {
    setAsking(true)
    register.mutate(
      {
        email: email.trim(),
        ...(name.trim() === '' ? {} : { name: name.trim() }),
        ...(invitation ? { invitation } : {}),
      },
      // Registering with a token is the redemption; nothing else has to accept
      // it afterwards. Told here, once, rather than left for whatever reads
      // the URL next to notice the token is now spent.
      invitation ? { onSuccess: () => onInvitationRedeemed?.() } : {},
    )
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    ask()
  }

  if (asking && register.error === null) {
    return (
      <Shell pitch={<Journey />}>
        <Ceremony
          email={email.trim()}
          pending={register.isPending}
          onRetry={ask}
          onChange={() => {
            setAsking(false)
            register.reset()
          }}
          making
        />
      </Shell>
    )
  }

  return (
    <Shell pitch={<Journey />}>
      <h1>{invited ? 'Accept the invitation' : 'Start your first statement'}</h1>
      <p className="nw-auth-lede">
        {invited ? (
          <>
            Accepting registers a passkey on this device and joins you to the household you were
            invited to — its figures, not a separate copy of them. Use the address the invitation
            was sent to; another one is refused.
          </>
        ) : open ? (
          <>
            Registering creates a household of your own, with a passkey on this device. Nobody else
            can see its figures, and you can invite people into it later.
          </>
        ) : (
          <>
            Accounts here are made from an invitation. Open the link an owner sent you and this page
            will accept it; if you already have an account, sign in instead.
          </>
        )}
      </p>

      {!authenticator && <NoAuthenticator />}

      <form onSubmit={submit}>
        <div className="nw-auth-field">
          <label htmlFor="nw-enrol-name">Your name</label>
          <input
            id="nw-enrol-name"
            type="text"
            autoComplete="name"
            aria-describedby="nw-enrol-name-hint"
            value={name}
            disabled={disabled || register.isPending}
            onChange={(event) => setName(event.target.value)}
          />
          <small id="nw-enrol-name-hint">
            {invited
              ? 'What the household sees beside your name.'
              : 'Also names the household this creates — left blank, it is named after the address. You can add more later.'}
          </small>
        </div>

        <div className="nw-auth-field">
          <label htmlFor="nw-enrol-email">Email</label>
          <input
            id="nw-enrol-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            disabled={disabled || register.isPending}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <button
          type="submit"
          className="nw-auth-submit"
          disabled={disabled || register.isPending || email.trim() === ''}
        >
          {invited ? 'Accept and register a passkey' : 'Create account and passkey'}
          <Arrow />
        </button>
      </form>

      {register.error && <RegistrationRefusal error={register.error} invited={invited} />}

      <Reassurance
        title="Your device will ask to make a passkey"
        body="Do this on a phone or laptop you keep. Add a second device from your Account page afterwards — it will go on warning you until you have one."
      />
      <Stakes advice={false} />

      <p className="nw-auth-swap">
        Already have an account?{' '}
        <button type="button" onClick={onSignIn}>
          Sign in
        </button>
      </p>
    </Shell>
  )
}

/**
 * The second step, and the only one this page does not drive: the browser is
 * holding its own dialog and the ceremony resolves when that dialog is answered.
 * Nothing here has to be pressed for a signed-in session to replace the page —
 * `useLogin` and `useRegister` adopt it and `App` swaps the tree — which is why
 * the copy can promise it moves on by itself.
 */
function Ceremony({
  email,
  pending,
  error = null,
  onRetry,
  onChange,
  making = false,
}: {
  email: string
  pending: boolean
  /** Only a sign-in shows one here; a refused registration belongs on its form. */
  error?: Error | null
  onRetry: () => void
  onChange: () => void
  making?: boolean
}): ReactElement {
  return (
    <>
      <p className="nw-auth-step">Step 2 of 2</p>

      <div className="nw-auth-who">
        <span>{email}</span>
        <button type="button" onClick={onChange}>
          Change
        </button>
      </div>

      <div className="nw-auth-badge" aria-hidden="true">
        <Key size={30} />
      </div>

      <h1>{making ? 'Make the passkey' : 'Confirm it is you'}</h1>
      <p className="nw-auth-lede">
        Your device is asking for your fingerprint, face or PIN.{' '}
        {making
          ? 'Approve it there and the key is made — this page will move on by itself.'
          : 'Approve it there and you are in — this page will move on by itself.'}
      </p>

      {pending && (
        <p className="nw-auth-progress" role="status">
          <span aria-hidden="true">
            <i />
          </span>
          <em>Waiting for your device</em>
        </p>
      )}

      {error && (
        <p className="nw-flag nw-flag--missing" style={{ marginTop: 22 }}>
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not sign you in: {describe(error)}</span>
        </p>
      )}

      <button type="button" className="nw-auth-quiet" disabled={pending} onClick={onRetry}>
        Ask again
      </button>

      <p className="nw-auth-key">
        The key never leaves your device. Networthy only receives a signature proving you approved
        it.
      </p>
    </>
  )
}

function Shell({ pitch, children }: { pitch: ReactNode; children: ReactNode }): ReactElement {
  return (
    <main className="nw nw-auth">
      <style>{chartCss + PAGE_CSS}</style>
      <div className="nw-auth-form">
        <p className="nw-auth-mark">
          <Logo />
          <b>Networthy</b>
        </p>
        <div className="nw-auth-body">{children}</div>
        <p className="nw-auth-footer">
          {OPERATOR} · <a href={TERMS_HREF}>Terms</a> · <a href={PRIVACY_HREF}>Privacy</a>
        </p>
      </div>
      <aside className="nw-auth-pitch">{pitch}</aside>
    </main>
  )
}

function NoAuthenticator(): ReactElement {
  return (
    <p className="nw-flag nw-flag--missing" style={{ marginTop: 20 }}>
      <span className="nw-flag-icon" aria-hidden="true">
        ✕
      </span>
      <span>
        <b>This browser cannot use passkeys</b> — and there is no password to fall back on. Open the
        dashboard in a browser that supports WebAuthn, over HTTPS or on localhost.
      </span>
    </p>
  )
}

function Reassurance({ title, body }: { title: string; body: string }): ReactElement {
  return (
    <div className="nw-auth-reassure">
      <Key size={19} />
      <span>
        <b>{title}</b>
        <span>{body}</span>
      </span>
    </div>
  )
}

/**
 * The advice to enrol twice is left out where the panel above has just given it,
 * which is every screen that is about to make a passkey rather than use one.
 */
function Stakes({ advice = true }: { advice?: boolean }): ReactElement {
  return (
    <div className="nw-auth-stakes">
      <p>
        <b>Losing every passkey means losing the history.</b> There is no password to reset and no
        account to recover. What is behind this door is years of daily snapshots assembled from
        banks, brokers and chains that will not hand any of it over a second time.
      </p>
      {advice && (
        <p>
          Register a second device before you need one. A passkey your platform syncs to your other
          devices survives that phone or laptop; one that lives only on this machine does not
          survive the machine.
        </p>
      )}
    </div>
  )
}

/** What signing in is for, beside the form. The figure is drawn and says so. */
function Assurance(): ReactElement {
  return (
    <>
      <div className="nw-auth-lead">
        <h2>Everything you own and owe, in one honest number.</h2>
        {/* The claim is about how exactly the figures are reconciled, and it was
            being made in one country's subunit — on the one page that is read
            before there is a household to have a currency at all. */}
        <p>Banks, brokers and wallets, read once a night and reconciled to the last decimal.</p>

        <div className="nw-auth-sample">
          <p>
            Total net worth <em>Example</em>
          </p>
          <p className="nw-auth-total">
            4.187.320<i>,45 kr</i>
          </p>
          <Sparkline />
        </div>

        <div className="nw-auth-points">
          <Point
            title="Your data is not the product"
            body="No analytics, no advertising, no tracking. Nothing is sold, and nothing is shared for anyone else's marketing."
          />
          <Point
            title="Every year-end exports as CSV"
            body="Every year end you have filed, as a file — the totals, or holding by holding."
          />
        </div>
      </div>
      <Reach />
    </>
  )
}

/** What creating an account leads to, beside the form. */
function Journey(): ReactElement {
  return (
    <>
      <div className="nw-auth-lead">
        <h2>Three steps to your first number.</h2>
        <p>Most households are done in about ten minutes.</p>

        <div className="nw-auth-steps">
          <Step
            n="1"
            title="Name and email, then a passkey"
            body="Your device creates the key. There is no password to choose, and none for us to lose."
          />
          <Step
            n="2"
            title="Connect what reports itself"
            body="Banks, brokers and exchanges, on whatever access each of them issues. Any of them can be revoked from the Sources page."
          />
          <Step
            n="3"
            title="Add the rest by hand"
            body="Property, a private stake, the car. Networthy marks a hand-entered value stale once it has aged."
          />
        </div>
      </div>
      <Reach />
    </>
  )
}

function Reach(): ReactElement {
  return (
    <p className="nw-auth-reach">
      <Lock />
      <span>{CONNECTION_REACH}</span>
    </p>
  )
}

function Point({ title, body }: { title: string; body: string }): ReactElement {
  return (
    <div>
      <Tick />
      <span>
        <b>{title}</b>
        <p>{body}</p>
      </span>
    </div>
  )
}

function Step({ n, title, body }: { n: string; title: string; body: string }): ReactElement {
  return (
    <div>
      <span aria-hidden="true">{n}</span>
      <span>
        <b>{title}</b>
        <p>{body}</p>
      </span>
    </div>
  )
}

function Logo(): ReactElement {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="22" height="22" rx="7" fill="var(--nw-accent)" />
      <path
        d="M6.5 15.5 L10 10.5 L13.5 13 L17.5 7.5"
        stroke="var(--nw-accent-ink)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Key({ size }: { size: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="12" r="4" />
      <line x1="12" y1="12" x2="21" y2="12" />
      <line x1="17.5" y1="12" x2="17.5" y2="15.5" />
      <line x1="20.5" y1="12" x2="20.5" y2="16.5" />
    </svg>
  )
}

function Lock(): ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
      <path d="M8 10.5 V7.5 a4 4 0 0 1 8 0 v3" />
    </svg>
  )
}

function Tick(): ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="8 12.5 11 15.5 16.5 9" />
    </svg>
  )
}

function Arrow(): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </svg>
  )
}

const SAMPLE_PATH =
  '0,227.2 87,223.1 173.9,191.4 260.9,175.5 347.8,153.8 434.8,137.3 521.7,113.1 608.7,98.8 695.7,73.2 782.6,62.2 869.6,60 956.5,31 1000,31.6'

function Sparkline(): ReactElement {
  return (
    <svg
      width="400"
      height="82"
      viewBox="0 0 1000 260"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {/* The same mark the line is drawn in, laid under it at a fraction — the
          wash was previously that colour's channels retyped by hand, which is
          how it would have survived the palette moving. */}
      <polygon
        points={`${SAMPLE_PATH} 1000,260 0,260`}
        fill="var(--nw-pitch-mark)"
        fillOpacity=".14"
      />
      <polyline
        points={SAMPLE_PATH}
        fill="none"
        stroke="var(--nw-pitch-mark)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function RegistrationRefusal({
  error,
  invited,
}: {
  error: Error
  invited: boolean
}): ReactElement {
  // One code covers every refusal to register: an invitation that does not hold,
  // a service that is not open to strangers, and an address that can already
  // sign in, which is refused a second registration so that typing somebody
  // else's address cannot enrol a passkey onto their account. The page cannot
  // tell those apart, so what it says has to be true of all of them and still
  // tell the reader what to do next.
  if (error.message === 'registration_closed') {
    return (
      <p className="nw-note" style={{ marginTop: 12 }}>
        {invited
          ? 'That invitation is spent, expired, or was written to a different address — or this address already has an account here, which is never registered a second time. If that is what happened, sign in instead and this invitation will be offered again once you are in.'
          : 'Accounts here are made from an invitation, so there is nothing to sign up for without one. If this address already has an account, sign in instead; an address that can already sign in is never registered a second time.'}
      </p>
    )
  }

  return (
    <p className="nw-flag nw-flag--missing" style={{ marginTop: 12 }}>
      <span className="nw-flag-icon" aria-hidden="true">
        ✕
      </span>
      <span>Could not register: {describe(error)}</span>
    </p>
  )
}

/** The API answers in codes; nobody reading this page speaks them. */
function describe(error: Error): string {
  switch (error.message) {
    case 'authentication_failed':
      return 'that passkey was not accepted for this address.'
    case 'challenge_expired':
      return 'the request took too long. Try again.'
    case 'credential_in_use':
      return 'that passkey is already enrolled here.'
    case 'credential_cloned':
      return 'the authenticator looks like a copy of one already enrolled, so it was refused.'
    default:
      return error.message
  }
}
